import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { isWithinInterval, subDays } from 'date-fns'
import { sortBy } from 'lodash-es'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import {
  getCurrentAppointment,
  getLastAppointment,
} from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { getClientHasActiveAiConversation } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import {
  getProgramIdFromName,
  getProgramNameFromId,
} from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { isIntakeSession } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { AssignmentStatus } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import { LTProgramName, ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import {
  ActivityStatus,
  ClientFiltersState,
  ClientFilterStatus,
  EpisodeProgress,
  NextSession,
  SessionType,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/clientFilters/ClientFiltersPopover'
import { AiCoachStatus as ClientFiltersAiCoachStatus } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/clientFilters/sections/ClientFiltersLyraAiSection'

import { episodeStates, programConfig } from '../../../../../common/constants/appConstants'
import { searchForClients } from '../../../../../common/utils/utils'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { useGetAssignmentStatusesQuery } from '../../../../assignments/data/assignmentsApi'
import { useConversationsData } from '../../../../messages/data/hooks/useConversationsData'
import { useGetAppointmentsQuery } from '../../../clientDetails/data/appointmentsApi'
import { useClientsData } from '../../../data/hooks/useClientsData'
import { useClientsInProgramData } from '../../../data/hooks/useClientsInProgramData'
import { ClientsInProgramFilter } from '../../../data/ltClientsApi'
import { ClientItem } from '../ClientListTable'
import { filterBy, getActivityStatus } from '../utils'

import type { GetAppointmentsArgs } from '../../../clientDetails/data/appointmentsApi'

const useFilteredClientItems = (
  searchText: string,
  filters: ClientFiltersState,
  isEngageDAProvider: boolean,
  dbtTeensFilter?: ClientsInProgramFilter,
): ClientItem[] | undefined => {
  const userId = useSelector(getAuthUserId)
  const { isCurrentEpisodeAppointmentsForClientListEnabled } = useFlags()

  const { data: clients } = useClientsData(
    {
      status: filters.status,
    },
    { refetchOnMountOrArgChange: true, skip: !!dbtTeensFilter },
  )

  const { data: dbtTeensClients } = useClientsInProgramData(
    {
      programName: ProgramNames.DialecticalBehaviorTherapyTeens,
      interactionMode: dbtTeensFilter,
    },
    { refetchOnMountOrArgChange: true, skip: !dbtTeensFilter },
  )

  const clientsToDisplay = dbtTeensFilter ? dbtTeensClients : clients

  const { data: aiCoachConversations } = useConversationsData({
    types: CONVERSATION_TYPE.AI_COACH,
  })

  const { data: assignmentsStatus } = useGetAssignmentStatusesQuery(
    { providerId: userId },
    {
      refetchOnMountOrArgChange: true,
      skip: !userId || isEngageDAProvider,
    },
  )
  const shouldRequestCurrentEpisodes =
    isCurrentEpisodeAppointmentsForClientListEnabled && !dbtTeensFilter && filters.status === ClientFilterStatus.ACTIVE
  const appointmentArgs: GetAppointmentsArgs = shouldRequestCurrentEpisodes
    ? { providerId: userId, episodes: 'current' }
    : { providerId: userId }
  const { currentData: appointments } = useGetAppointmentsQuery(appointmentArgs, {
    refetchOnMountOrArgChange: true,
  })

  const allClients = useMemo(
    () =>
      clientsToDisplay?.map((client) => {
        const appointsmentsForClient = appointments?.filter((appt) => appt.userId === client.id)

        const lastAppointment =
          appointsmentsForClient !== undefined ? (getLastAppointment(appointsmentsForClient ?? []) ?? null) : undefined
        const nextAppointment =
          appointsmentsForClient !== undefined
            ? (getCurrentAppointment(appointsmentsForClient ?? []) ?? null)
            : undefined

        const assignmentsForClient: AssignmentStatus[] | undefined | null =
          assignmentsStatus !== undefined ? (assignmentsStatus?.[client.id] ?? null) : undefined

        const hasActiveAiCoachConversation = getClientHasActiveAiConversation(client.id, aiCoachConversations)
        return {
          ...client,
          nextAppointment,
          lastAppointment,
          activityStatus: getActivityStatus(assignmentsForClient, nextAppointment),
          isAiCoachEnabled: hasActiveAiCoachConversation,
        }
      }),
    [aiCoachConversations, appointments, assignmentsStatus, clientsToDisplay],
  )

  return useMemo(() => {
    const trimmedSearchText = searchText.trim()
    const clientsToSearch =
      trimmedSearchText.length > 0
        ? searchForClients({
            clients: allClients,
            showAll: true,
            searchValue: trimmedSearchText,
          })
        : allClients

    return clientsToSearch?.filter((client) => {
      if (filters.programs.length > 0) {
        const passesProgramFilters = filters.programs.some((program) => {
          if (client?.program_name) {
            return getProgramIdFromName(client.program_name) === program
          } else {
            return false
          }
        })
        if (!passesProgramFilters) {
          return false
        }
      }

      if (filters.nextSession.length > 0) {
        const passesNextSessionFilters = filterBy(filters.nextSession, {
          [NextSession.SCHEDULED]: () => client.nextAppointment != null,
          [NextSession.UNSCHEDULED_LESS_30_DAYS]: () => {
            if (client.nextAppointment != null || client.lastAppointment == null) {
              return false
            }
            return isWithinInterval(getDateFromAppointment(client.lastAppointment), {
              start: subDays(new Date(), 30),
              end: new Date(),
            })
          },
          [NextSession.UNSCHEDULED_MORE_30_DAYS]: () => {
            if (client.nextAppointment != null) {
              return false
            }
            if (client.lastAppointment == null) {
              return true
            }
            return !isWithinInterval(getDateFromAppointment(client.lastAppointment), {
              start: subDays(new Date(), 30),
              end: new Date(),
            })
          },
        })
        if (!passesNextSessionFilters) {
          return false
        }
      }
      if (filters?.episodeProgress && filters.episodeProgress.length > 0) {
        const passesEpisodeProgressFilters = filterBy(filters.episodeProgress, {
          [EpisodeProgress.LONGER]: () => {
            const inProgressClientAppointments = sortBy(
              appointments?.filter(
                (appt) => appt.userId === client.id && appt.episodeState === episodeStates.IN_PROGRESS,
              ),
              (appt) => appt.sessionNumber,
            )
            if (inProgressClientAppointments.length === 0) {
              return false
            }
            const lastAppointment = inProgressClientAppointments[inProgressClientAppointments.length - 1]
            const lastAppointmentProgramName = getProgramNameFromId(lastAppointment.programId)
            const recommendedEpisodeLength = lastAppointmentProgramName
              ? programConfig[lastAppointmentProgramName as LTProgramName]?.recommendedEpisodeLength
              : undefined

            if (!recommendedEpisodeLength) {
              return false
            }

            return lastAppointment.sessionNumber > recommendedEpisodeLength
          },
        })
        if (!passesEpisodeProgressFilters) {
          return false
        }
      }

      if (filters?.sessionType && filters.sessionType.length > 0) {
        const passesSessionTypeFilters = filterBy(filters.sessionType, {
          [SessionType.INTAKE]: () => client.nextAppointment != null && isIntakeSession(client.nextAppointment),
          [SessionType.FOLLOW_UP]: () => client.nextAppointment != null && !isIntakeSession(client.nextAppointment),
        })
        if (!passesSessionTypeFilters) {
          return false
        }
      }

      if (filters?.activityStatus && filters.activityStatus.length > 0) {
        const passesActivityStatusFilters = filterBy(filters.activityStatus, {
          [ActivityStatus.COMPLETE]: () => client.activityStatus?.status === ActivityStatus.COMPLETE,
          [ActivityStatus.PENDING]: () => client.activityStatus?.status === ActivityStatus.PENDING,
          [ActivityStatus.INCOMPLETE]: () => client.activityStatus?.status === ActivityStatus.INCOMPLETE,
          [ActivityStatus.NOT_SENT]: () => client.activityStatus?.status === ActivityStatus.NOT_SENT,
        })
        if (!passesActivityStatusFilters) {
          return false
        }
      }

      if (filters?.modality && filters.modality.length > 0) {
        const passesModalityFilters = filterBy(filters.modality, {
          [MEETING_FORMATS.IN_PERSON]: () =>
            client.nextAppointment != null && client.nextAppointment.meetingFormat === MEETING_FORMATS.IN_PERSON,
          [MEETING_FORMATS.VIDEO]: () =>
            client.nextAppointment != null && client.nextAppointment.meetingFormat === MEETING_FORMATS.VIDEO,
          [MEETING_FORMATS.LIVE_MESSAGING]: () =>
            client.nextAppointment != null && client.nextAppointment.meetingFormat === MEETING_FORMATS.LIVE_MESSAGING,
        })
        if (!passesModalityFilters) {
          return false
        }
      }

      if (filters?.aiCoachStatus && filters.aiCoachStatus.length > 0) {
        const passesAiCoachFilters = filterBy(filters.aiCoachStatus, {
          [ClientFiltersAiCoachStatus.ENABLED]: () => client.isAiCoachEnabled,
        })
        if (!passesAiCoachFilters) {
          return false
        }
      }

      return true
    })
  }, [allClients, appointments, filters, searchText])
}

export default useFilteredClientItems
